/**
 * DataEntryWorkspace — the presentational pieces of «הזנה חכמה» in the record-card language
 * (EntityCard; ADR-0037, Turbo's DataEntryWorkspace): collapsible key–value sections for the side
 * column, and the tab bodies. All state and actions live in DataEntryAgent.
 */
import { Fragment, useCallback, useState, type ReactNode } from "react";
import {
  Banner,
  Button,
  ChoiceCards,
  ComboBox,
  EmptyState,
  FileDrop,
  Icon,
  IconButton,
  Kbd,
  ProgressBar,
  Select,
  StatGrid,
  StatTile,
  Tag,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import {
  aiDataEntryApi,
  type ColumnGuessDto,
  type ColumnRole,
  type CounterpartyDto,
  type DataEntrySessionDto,
  type ImportType,
  type PaymentMethod,
  type RecurrenceGroupDto,
  type SavedRuleDto,
} from "../../../api/aiDataEntryApi";
import {
  FLAG_LABELS,
  IMPORT_TYPE_OPTIONS,
  isEntityImport,
  isFinished,
  KIND_META,
  KIND_ORDER,
  MATCH_LABELS,
  money,
  PAYMENT_OPTIONS,
  rowState,
  shortDate,
  STATE_META,
  type LocalRow,
} from "./dataEntryModel";

/** a side-column section, same markup as the record card's FieldSection */
export function DeSection({ title, children, defaultOpen = true, end }: { title: ReactNode; children?: ReactNode; defaultOpen?: boolean; end?: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`ck-sec ${open ? "" : "is-closed"}`}>
      <div className="ck-sec__h">
        <button type="button" className="ck-sec__toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <Icon name="down" size={14} className="ck-sec__chev" />
          <b>{title}</b>
        </button>
        {end}
      </div>
      {open ? <div className="ck-sec__b">{children}</div> : null}
    </div>
  );
}

export function KV({ items }: { items: Array<{ label: string; value: ReactNode; ltr?: boolean } | null | false> }) {
  const shown = items.filter((x): x is { label: string; value: ReactNode; ltr?: boolean } => Boolean(x) && (x as { value: unknown }).value !== null && (x as { value: unknown }).value !== undefined && (x as { value: unknown }).value !== "");
  if (!shown.length) return <div className="ck-sec__muted">אין נתונים</div>;
  return (
    <dl className="ck-kv">
      {shown.map((x) => (
        <Fragment key={x.label}>
          <dt>{x.label}</dt>
          <dd className={x.ltr ? "de-ltr" : ""}>{x.value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

/* ── before a file ── */

const FILE_KINDS = ["דף בנק", "תשלומים של לקוחות", "הכנסות והוצאות", "הוצאות לספקים", "מכירות / הרשמות", "קובץ מעורב"];

export function UploadTab({ busy, onFile, error, onAudit }: { busy: boolean; onFile: (f: File) => void; error: string | null; onAudit?: () => void }) {
  return (
    <div className="de-pane de-upload">
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <FileDrop accept=".xlsx,.xls,.csv" loading={busy} onFile={onFile} title="גררו לכאן קובץ, או לחצו לבחירה" hint="Excel או CSV · עד 5,000 שורות · 10MB" />
      <div className="de-upload__kinds">
        <span className="de-muted">הסוכן מכיר:</span>
        {FILE_KINDS.map((k) => (
          <Tag key={k}>{k}</Tag>
        ))}
      </div>
      {onAudit ? (
        <button type="button" className="de-audit-entry" onClick={onAudit}>
          <span className="de-audit-entry__ic" aria-hidden="true">
            ✦
          </span>
          <span className="de-audit-entry__t">
            <b>או: בדיקת הנתונים שכבר במערכת</b>
            <span className="de-muted">בלי קובץ — הסוכן עובר על התשלומים, ההכנסות וההוצאות, מוצא פערים, רשומות בלי שיוך וכפילויות, ומתקן באישורכם.</span>
          </span>
          <Icon name="left" size={16} />
        </button>
      ) : null}
    </div>
  );
}

export function ChatTips() {
  const tips: Array<[string, string]> = [
    ["הנחיה", "«כל השורות שכתוב בהן ביט — הכנסות לקטגוריה כללי»"],
    ["עמודה", "«עמודה ג׳ היא הנציג, לא התיאור»"],
    ["תיקון", "«טעית, זה משה כהן מבני ברק»"],
    ["הסבר", "«למה שורה 12 סווגה כהוצאה?»"],
    ["סיכום", "«כמה כסף נכנס בכל חודש?»"],
    ["לזכור", "«תמיד: ספק בזק = תקשורת»"],
  ];
  return (
    <DeSection title="מה אפשר לבקש">
      <ol className="de-how">
        {tips.map(([t, d]) => (
          <li key={t}>
            <b>{t}</b>
            <span>{d}</span>
          </li>
        ))}
      </ol>
      <div className="ck-sec__muted">הסוכן משנה רק את השורות על המסך. נרשם במערכת רק מה שאישרתם.</div>
    </DeSection>
  );
}

export function HowItWorks() {
  const steps: Array<[string, string]> = [
    ["קורא", "מוצא את שורת הכותרות, הגיליונות והעמודות — גם בקבצים «מבולגנים»"],
    ["מזהה", "לכל שורה: תשלום מלקוח, הכנסה, הוצאה לספק או מכירה חדשה — ומה כבר קיים במערכת"],
    ["משייך", "ללקוח או לספק — לפי טלפון, ת.ז, מייל, קוד לקוח או שם; ולעסקה פתוחה של הלקוח"],
    ["מתריע", "כפילויות, רשומות קיימות, תאריכים חסרים, התאמות לא ודאיות"],
    ["רושם", "רק מה שאישרתם — דרך המסכים הרגילים של המערכת, עם ביטול לכל שורה"],
  ];
  return (
    <DeSection title="מה הסוכן עושה">
      <ol className="de-how">
        {steps.map(([t, d]) => (
          <li key={t}>
            <b>{t}</b>
            <span>{d}</span>
          </li>
        ))}
      </ol>
      <div className="ck-sec__muted">
        <Kbd>Enter</Kbd> אישור והבאה · <Kbd>Shift</Kbd>+<Kbd>Enter</Kbd> אחורה
      </div>
    </DeSection>
  );
}

export function SavedRulesSection({ rules, onRemove, defaultOpen = false }: { rules: SavedRuleDto[]; onRemove?: (r: SavedRuleDto) => void; defaultOpen?: boolean }) {
  return (
    <DeSection title={`כללים שמורים${rules.length ? ` · ${rules.length}` : ""}`} defaultOpen={defaultOpen}>
      {rules.length ? (
        <ul className="de-rules">
          {rules.map((r) => (
            <li key={r.id}>
              <span>
                {r.label}
                {r.ledgerId ? <small className="de-muted"> · לקופה הזו</small> : null}
              </span>
              {onRemove ? <IconButton size="sm" icon="trash" label="מחיקת הכלל" onClick={() => onRemove(r)} /> : null}
            </li>
          ))}
        </ul>
      ) : (
        <div className="ck-sec__muted">עוד אין כללים. כשתתקנו שורה, הסוכן יציע לזכור את התיקון לקבצים הבאים.</div>
      )}
    </DeSection>
  );
}

/* ── the file ── */

export function FileSection({ upload, onSheet, sheetBusy }: { upload: DataEntrySessionDto; onSheet?: (sheet: number) => void; sheetBusy?: boolean }) {
  return (
    <DeSection title="הקובץ">
      <KV
        items={[
          { label: "שם", value: upload.fileName },
          { label: "שורות", value: upload.totalRows.toLocaleString("he-IL") },
          { label: "עמודות", value: upload.headers.length },
          { label: "זוהה כ", value: upload.insights?.profile.label ?? (upload.isBank ? "דף בנק" : null) },
          { label: "AI", value: upload.ai.note ? "כבוי — כללים והיסטוריה" : "פעיל" },
        ]}
      />
      {upload.sheets.length > 1 ? (
        <div className="de-field-inline">
          <span className="ck-sec__k">גיליון</span>
          <Select
            value={String(upload.sheet)}
            disabled={sheetBusy || !onSheet}
            onChange={(v) => v !== "" && onSheet?.(Number(v))}
            options={upload.sheets.map((sh) => ({ value: String(sh.index), label: `${sh.name} · ${sh.rows} שורות` }))}
            aria-label="בחירת גיליון"
          />
        </div>
      ) : null}
    </DeSection>
  );
}

/** the ledger (קופה) incomes / expenses go into — searched like any customer */
export function LedgerPicker({ sessionId, ledger, onChange, disabled }: { sessionId: string; ledger: DataEntrySessionDto["ledger"]; onChange: (id: string | null) => void; disabled?: boolean }) {
  const search = useCallback(async (q: string) => (q.length < 2 ? [] : aiDataEntryApi.search(sessionId, "customer", q)), [sessionId]);
  return (
    <div className="de-ledger">
      {ledger ? (
        <div className="de-ledger__cur">
          <Tag tone="primary" icon="wallet">
            {ledger.name}
          </Tag>
          {disabled ? null : <IconButton size="sm" icon="x" label="ביטול הקופה" onClick={() => onChange(null)} />}
        </div>
      ) : null}
      {disabled ? null : (
        <ComboBox
          value={null}
          onChange={(o: CounterpartyDto | null) => o && onChange(o.id)}
          onSearch={search}
          minChars={2}
          placeholder={ledger ? "החלפת קופה…" : "חיפוש קופה (לקוח)…"}
          ariaLabel="קופה להכנסות והוצאות"
          getKey={(o: CounterpartyDto) => o.id}
          renderOption={(o: CounterpartyDto) => (
            <span className="de-cand__t">
              <b>{o.label}</b>
              {o.sub ? <small>{o.sub}</small> : null}
            </span>
          )}
        />
      )}
    </div>
  );
}

export function DefaultsSection({
  upload,
  onLedger,
  onDefaults,
  ledgerLocked,
}: {
  upload: DataEntrySessionDto;
  onLedger: (id: string | null) => void;
  onDefaults: (d: Partial<DataEntrySessionDto["defaults"]>) => void;
  ledgerLocked?: boolean;
}) {
  const d = upload.defaults;
  return (
    <DeSection title="ברירות מחדל לכל הקובץ">
      <div className="de-stack">
        <div className="de-field-inline">
          <span className="ck-sec__k">קופה (הכנסות / הוצאות)</span>
          <LedgerPicker sessionId={upload.id} ledger={upload.ledger} onChange={onLedger} disabled={ledgerLocked} />
        </div>
        <label className="de-field-inline">
          <span className="ck-sec__k">קטגוריה</span>
          <Select
            value={d.categoryId ?? ""}
            placeholder="לפי הקובץ / היסטוריה"
            options={[{ value: "", label: "לפי הקובץ / היסטוריה" }, ...upload.lookups.categories.map((c) => ({ value: c.id, label: c.name }))]}
            onChange={(v) => onDefaults({ categoryId: v || null })}
          />
        </label>
        {upload.ledger ? (
          <label className="de-field-inline">
            <span className="ck-sec__k">קרן</span>
            <Select
              value={d.fundId ?? ""}
              placeholder="ברירת מחדל"
              options={[{ value: "", label: "ברירת מחדל" }, ...upload.lookups.funds.map((f) => ({ value: f.id, label: f.name }))]}
              onChange={(v) => onDefaults({ fundId: v || null })}
            />
          </label>
        ) : null}
        <label className="de-field-inline">
          <span className="ck-sec__k">אמצעי תשלום</span>
          <Select
            value={d.paymentMethod ?? ""}
            placeholder={upload.isBank ? "העברה בנקאית" : "לבחור בכל שורה"}
            options={[{ value: "", label: "לבחור בכל שורה" }, ...PAYMENT_OPTIONS]}
            onChange={(v) => onDefaults({ paymentMethod: (v || null) as PaymentMethod | null })}
          />
        </label>
      </div>
      <div className="ck-sec__muted">חל רק על שורות שיירשמו כחדשות — אפשר לשנות בכל שורה.</div>
    </DeSection>
  );
}

export function TypeTab({ upload, importType, setImportType, columns }: { upload: DataEntrySessionDto; importType: ImportType; setImportType: (t: ImportType) => void; columns: ColumnGuessDto[] }) {
  const kinds = upload.lookups.kinds;
  const targets = upload.lookups.entityTargets ?? [];
  const options = IMPORT_TYPE_OPTIONS.filter(
    (o) =>
      (o.value !== "sales" || kinds.includes("sale")) &&
      (o.value !== "incomes" || kinds.includes("income")) &&
      (o.value !== "expenses" || kinds.includes("expense")) &&
      (o.value !== "payments" || kinds.includes("payment")) &&
      (o.value !== "customers" || targets.includes("customers")) &&
      (o.value !== "suppliers" || targets.includes("suppliers")),
  ).map((o) => (o.value === upload.suggestedType ? { ...o, label: `${o.label} · מומלץ` } : o));
  const hasAmount = columns.some((c) => c.role === "amount" || c.role === "credit" || c.role === "debit");
  const list = isEntityImport(importType);
  const hasName = columns.some((c) => c.role === "name");
  return (
    <div className="de-pane">
      {upload.ai.note ? <Banner tone="info">{upload.ai.note}</Banner> : null}
      {upload.ai.error ? <Banner tone="warning">ה-AI לא זמין כרגע. הזיהוי ימשיך לפי כללים והיסטוריה.</Banner> : null}
      {upload.recurring?.applied ? (
        <Banner tone="success" icon="repeat">
          <b>זוהתה העלאה קבועה «{upload.recurring.name}».</b> ההגדרות מהפעם הקודמת הוחלו — סוג, עמודות, קופה, ברירות מחדל וכללים. המועד הבא: {shortDate(upload.recurring.nextDueDate)}.
        </Banner>
      ) : null}
      {!hasAmount && !list ? (
        <Banner tone="warning">לא זוהתה עמודת סכום — סמנו בלשונית «עמודות» איזו עמודה היא הסכום, או בחרו «רשימת לקוחות» אם בקובץ אין תנועות.</Banner>
      ) : null}
      {list ? (
        <Banner tone={hasName ? "info" : "warning"}>
          {hasName ? (
            <>
              <b>רשימה, לא תנועות:</b> כל שורה היא {importType === "customers" ? "לקוח" : "ספק"}. הסוכן בודק כל שורה מול המערכת (טלפון, ת.ז, מייל, קוד ושם) ומציע ליצור,
              לדלג או להשלים את הרשומה הקיימת — אתם מאשרים, וכל רישום ניתן לביטול.
            </>
          ) : (
            "לא זוהתה עמודת שם — סמנו בלשונית «עמודות» איזו עמודה היא השם."
          )}
        </Banner>
      ) : null}
      {list && importType === "suppliers" && !upload.ledger ? <Banner tone="warning">ספקים שייכים לקופה — בחרו קופה בצד («ברירות מחדל»).</Banner> : null}
      {!list && !upload.ledger && (kinds.includes("income") || kinds.includes("expense")) ? (
        <Banner tone="info">
          להכנסות ולהוצאות צריך לבחור <b>קופה</b> (בצד, «ברירות מחדל»). תשלומים של לקוחות ומכירות נרשמים גם בלי קופה.
        </Banner>
      ) : null}
      <div className="de-pane__h">
        <b>מה יש בקובץ?</b>
        <span className="de-muted">תנועות כספיות — הסוכן מזהה שורה-שורה, או שתקבעו סוג אחד לכל הקובץ · או רשימה של לקוחות / ספקים</span>
      </div>
      <ChoiceCards ariaLabel="סוג הקובץ" columns={3} value={importType} onChange={setImportType} options={options} />
    </div>
  );
}

export function ColumnsTab({ upload, columns, setColumns }: { upload: DataEntrySessionDto; columns: ColumnGuessDto[]; setColumns: (fn: (cs: ColumnGuessDto[]) => ColumnGuessDto[]) => void }) {
  const roleOptions = (Object.keys(upload.roles) as ColumnRole[]).map((value) => ({ value, label: upload.roles[value] }));
  return (
    <div className="de-pane">
      <div className="de-pane__h">
        <b>העמודות שזוהו</b>
        <span className="de-muted">תקנו רק אם משהו לא נכון — דוגמאות מהשורות הראשונות</span>
      </div>
      <div className="de-cols">
        {columns.map((c) => {
          const weak = c.source === "heuristic" && c.confidence < 0.6 && c.role !== "ignore";
          return (
            <div key={c.header} className={`de-col ${c.role === "ignore" ? "is-off" : ""} ${weak ? "is-weak" : ""}`}>
              <div className="de-col__h">
                <b title={c.header}>{c.header}</b>
                {c.source === "ai" ? (
                  <Tag tone="primary" icon="sparkle">
                    AI
                  </Tag>
                ) : null}
                {weak ? <Tag tone="warning">לא בטוח</Tag> : null}
              </div>
              <Select
                value={c.role}
                options={roleOptions}
                onChange={(v) => setColumns((cs) => cs.map((x) => (x.header === c.header ? { ...x, role: (v || "ignore") as ColumnRole, confidence: 1, source: "user" } : x)))}
                aria-label={`תפקיד העמודה ${c.header}`}
              />
              <span className="de-muted de-col__s">{c.samples.slice(0, 3).join(" / ") || "ריקה"}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── the current row (side column) ── */

export function RowFileSection({ row, roles }: { row: LocalRow; roles: Record<string, string> }) {
  return (
    <DeSection title={`השורה בקובץ · ${row.line}`}>
      <dl className="ck-kv de-raw-kv">
        {row.raw.map((c) => (
          <Fragment key={c.header}>
            <dt className={c.role !== "ignore" ? "is-used" : ""} title={c.role !== "ignore" ? roles[c.role] : "לא בשימוש"}>
              {c.header}
              {c.role !== "ignore" && roles[c.role] && roles[c.role] !== c.header ? <small> · {roles[c.role]}</small> : null}
            </dt>
            <dd>{c.value}</dd>
          </Fragment>
        ))}
      </dl>
    </DeSection>
  );
}

export function RowUnderstandingSection({ row }: { row: LocalRow }) {
  const meta = KIND_META[row.kind];
  const flags = row.flags.filter((f) => FLAG_LABELS[f] && !["new", "existing_any", "skipped"].includes(f));
  return (
    <DeSection title="מה הסוכן הבין">
      <KV
        items={[
          {
            label: "סוג",
            value: (
              <span className="de-kv-tag">
                <Tag tone={meta.tone} icon={meta.icon}>
                  {meta.label}
                </Tag>
                {row.kindConfidence ? <small className="de-muted">{Math.round(row.kindConfidence * 100)}%</small> : null}
              </span>
            ),
          },
          { label: "למה", value: row.kindReason },
          {
            label: "שיוך",
            value: row.counterparty
              ? `${row.counterparty.label}${MATCH_LABELS[row.counterparty.matchType] ? ` · ${MATCH_LABELS[row.counterparty.matchType]}` : ""}`
              : row.newEntity?.name
                ? `חדש: ${row.newEntity.name}`
                : null,
          },
          { label: "כלל", value: row.ruleApplied },
          { label: "פעם קודמת", value: row.lastSeen?.date ? `${money(row.lastSeen.amount)} · ${shortDate(row.lastSeen.date)}` : null },
          { label: "מחזוריות", value: row.recurrence?.label },
          { label: "AI", value: row.ai?.why ? `${row.ai.why}${row.ai.confidence ? ` (${Math.round(row.ai.confidence * 100)}%)` : ""}` : null },
        ]}
      />
      {flags.length ? (
        <div className="ck-sec__flags">
          {flags.map((f) => (
            <Tag key={f} tone={/dup|ambig|no_date|no_ledger|negative|partial/.test(f) ? "warning" : "neutral"}>
              {FLAG_LABELS[f]}
            </Tag>
          ))}
        </div>
      ) : null}
    </DeSection>
  );
}

export function NearbySection({ rows, cur, onPick }: { rows: LocalRow[]; cur: number; onPick: (i: number) => void }) {
  const from = Math.max(0, cur - 3);
  return (
    <DeSection title="שורות סמוכות">
      <div className="de-mini" aria-label="שורות סמוכות">
        {rows.slice(from, cur + 8).map((r) => {
          const st = rowState(r);
          return (
            <button key={r.index} type="button" className={`de-mini__i ${r.index === cur ? "is-cur" : ""}`} onClick={() => onPick(r.index)}>
              <span className={`de-dot ck-tone-${STATE_META[st].tone}`} title={STATE_META[st].label} />
              <span className="de-mini__t">{r.counterparty?.label || r.newEntity?.name || r.source || `שורה ${r.line}`}</span>
              <span className={`de-num de-num--${KIND_META[r.kind].dir ?? "none"}`}>{money(r.amount, r.currency)}</span>
            </button>
          );
        })}
      </div>
    </DeSection>
  );
}

export type Counts = { total: number; done: number; skipped: number; review: number; ready: number; error: number; saving: number; kept: number };

export function ProgressSection({ counts, progress, filterLabel, onClearFilter }: { counts: Counts; progress: number; filterLabel: string | null; onClearFilter: () => void }) {
  return (
    <DeSection title="התקדמות">
      <ProgressBar value={progress} tone="success" label="התקדמות" />
      <KV
        items={[
          { label: "נרשמו", value: counts.done || null },
          { label: "דולגו / ללא שינוי", value: counts.skipped || null },
          { label: "נותרו", value: counts.review + counts.ready + counts.error },
        ]}
      />
      {filterLabel ? (
        <div className="de-filterchip">
          <Tag tone="primary" icon="filter">
            {filterLabel}
          </Tag>
          <button type="button" className="ck-link" onClick={onClearFilter}>
            הצגת כל השורות לטיפול
          </button>
        </div>
      ) : null}
    </DeSection>
  );
}

/* ── summary tab ── */

export function GroupsPanel({ groups, rows }: { groups: RecurrenceGroupDto[]; rows: LocalRow[] }) {
  if (!groups.length) return null;
  return (
    <div className="de-block">
      <div className="de-pane__h">
        <b>תשלומים מחזוריים שזוהו</b>
        <span className="de-muted">{groups.length} קבוצות — אותו סכום, אותו מרווח</span>
      </div>
      <div className="de-groups">
        {groups.map((g) => {
          const kindLabel = KIND_META[g.kind].label;
          const doneRows = g.rowIndexes.filter((i) => isFinished(rows[i])).length;
          return (
            <div key={g.id} className="de-group">
              <div className="de-group__t">
                <b>{g.label || kindLabel}</b>
                <span className="de-muted">
                  {kindLabel} · {g.frequencyLabel} · {money(g.amount)} · {g.count} פעמים ({shortDate(g.firstDate)}–{shortDate(g.lastDate)}) · {doneRows} נרשמו
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SummaryTab({ rows, counts, pendingCount, groups, onDownload, onBackToRows }: { rows: LocalRow[]; counts: Counts; pendingCount: number; groups: RecurrenceGroupDto[]; onDownload: () => void; onBackToRows: () => void }) {
  return (
    <div className="de-pane">
      <Banner tone={counts.error ? "warning" : counts.done ? "success" : "info"}>
        <b>{counts.done ? `${counts.done} רשומות חדשות נרשמו. ` : "עדיין לא נרשמו שורות. "}</b>
        {pendingCount ? `${pendingCount} שורות לא אושרו ונשארו בצד.` : "כל הקובץ טופל."}
      </Banner>
      <StatGrid min={140}>
        {KIND_ORDER.filter((k) => k !== "skip").map((k) => {
          const list = rows.filter((r) => r.kind === k && rowState(r) === "done");
          return list.length ? (
            <StatTile key={k} label={KIND_META[k].label} value={list.length} sub={money(list.reduce((a, r) => a + (Number(r.amount) || 0), 0))} icon={KIND_META[k].icon} tone={KIND_META[k].tone} />
          ) : null;
        })}
        <StatTile label="על עסקה פתוחה" value={rows.filter((r) => r.commit.state === "done" && r.commit.result.deal).length} icon="link" tone="violet" />
        <StatTile label="דולגו / ללא שינוי" value={counts.skipped} icon="eyeOff" />
      </StatGrid>
      <GroupsPanel groups={groups} rows={rows} />
      <div className="de-actions-row">
        {pendingCount ? (
          <Button icon="right" onClick={onBackToRows}>
            חזרה ל-{pendingCount} השורות שנותרו
          </Button>
        ) : null}
        {pendingCount || counts.error ? (
          <Button icon="download" onClick={onDownload}>
            הורדת השורות שלא נרשמו
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function EmptyFilter({ onClear }: { onClear: () => void }) {
  return <EmptyState icon="check" title="אין שורות בסינון הזה" action={<Button onClick={onClear}>כל השורות לטיפול</Button>} />;
}
